import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import type { ProduccionCabecera, PlanificacionSemanalRow } from '@/types'
import type { TimeTrack } from '@/utils/planningTimeTracks'
import { fmt, pct, parseDate, sd } from '@/utils/planningFormatters'
import { ConsumoSubTable } from '@/components/planning/ConsumoSubTable'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import { BotonMarca, useMarcada, useMarcasFilas } from '@/components/common/MarcasFilas'
import { claveFila } from '@/utils/marcasFilas'
import { asEstadoMaterial, cuentaComoRotura, peorEstado } from '@/utils/estadoMaterial'
import { COMP_BADGE } from '@/components/production-sequence/sequenceFormat'
import { CarrilTrack } from '@/components/planning/CarrilTrack'
import { INFO_COL_WIDTH as ANCHO_INFO } from '@/components/planning/ganttLayout'

// `INFO_COL_WIDTH` se mudo a `ganttLayout.ts`: la cabecera la importaba desde
// aqui, y una constante de layout no puede depender de uno de sus dos usuarios.
export { INFO_COL_WIDTH } from '@/components/planning/ganttLayout'

/**
 * Una fila del Gantt: panel de información fijo + carril de barras.
 *
 * Es agnóstica al rango temporal: toda la geometría (posición de la barra, línea
 * de hoy, rejilla, umbral de rótulo) la aporta el `track`, así que sirve igual
 * para la vista semanal, mensual y anual.
 */
export function GanttRow({ prod, items, track, dark, onSelectComponent, highlighted, isEmpresaAll, autoAbrir, resalta, busquedaNonce }: {
  prod: ProduccionCabecera
  items: PlanificacionSemanalRow[]
  track: TimeTrack
  dark: boolean
  onSelectComponent: (item: PlanificacionSemanalRow) => void
  highlighted?: boolean
  isEmpresaAll: boolean
  /** La OP ha salido en la busqueda POR UN COMPONENTE: abre sola su subtabla. */
  autoAbrir?: boolean
  /** Que componentes van resaltados dentro de la subtabla. */
  resalta?: ((item: PlanificacionSemanalRow) => boolean) | null
  /** Firma de la busqueda. Al cambiar se olvidan los despliegues manuales. */
  busquedaNonce?: string
}) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  // La fila del Gantt NO es un `<tr>` —es un div con panel fijo y carril de
  // barras—, asi que no puede usar `FilaMarcable`: se pinta a mano el tinte y se
  // coloca `BotonMarca` en el canalon del chevron.
  const marcas = useMarcasFilas('gantt')
  // La OP identifica la fila en el negocio. Va con la empresa por delante porque
  // el mismo prodid puede existir en dos entidades legales y la vista se
  // consulta con «todas las empresas».
  const claveOp = claveFila(prod.empresa, prod.produccion)
  const marcada = useMarcada(marcas, claveOp)
  // Solo lo que el usuario ha tocado; el resto sigue al despliegue automatico.
  const [manual, setManual] = useState<boolean | null>(null)
  // Con un filtro nuevo manda otra vez el criterio automatico: si no, la OP que
  // acaba de salir seguiria cerrada porque se cerro hace cinco minutos con otra
  // busqueda puesta. A partir de ahi el usuario puede colapsarla a mano.
  useEffect(() => { setManual(null) }, [busquedaNonce])
  const open = manual ?? !!autoAbrir
  const unidades   = Number(prod.unidades)
  const pendientes = Number(prod.unidadesPendientes)
  const avancePct  = pct(unidades - pendientes, unidades)

  const roturaCount = items.filter(r => cuentaComoRotura(r)).length
  const hasRotura   = roturaCount > 0

  // El semáforo de la ORDEN a partir de los de sus componentes: manda el peor.
  // Es la misma regla que usa Secuencia (`peorEstado`), importada y no
  // reescrita: dos copias de esta regla acabarían discrepando sobre la misma
  // orden y nadie sabría a cuál creer.
  //
  // Una orden con nueve componentes en el almacén y uno sin comprar no está
  // «casi bien»: no se puede producir.
  const estadoMaterial = useMemo(() => {
    const estados = items
      .map(r => asEstadoMaterial(r.estadoMaterial))
      .filter((e): e is NonNullable<typeof e> => !!e)
    return estados.length > 0 ? peorEstado(estados) : undefined
  }, [items])

  // Cuantos de sus componentes casan con la busqueda. La chapa la lleva TODA
  // fila que haya salido por un componente, este abierta o no: el despliegue
  // automatico tiene tope de legibilidad y en la vista anual la mayoria quedan
  // cerradas. Sin la chapa el usuario ve una OP cuyo codigo y articulo no se
  // parecen en nada a lo que busco y no sabe por que esta ahi.
  const coincidenciasComp = useMemo(
    () => (resalta ? items.filter(resalta).length : 0),
    [items, resalta],
  )

  const isDelayed = useMemo(() => {
    const fechaFin = parseDate(prod.fechaFin)
    const today = new Date(); today.setHours(0, 0, 0, 0)
    return !!fechaFin && fechaFin < today && pendientes > 0
  }, [prod.fechaFin, pendientes])

  const d      = sd(prod.status)
  const pos    = track.pos(parseDate(prod.fechaInicio), parseDate(prod.fechaFin))
  const todayX = track.todayX

  const rowBg = hasRotura
    ? (dark ? 'bg-red-950/15 hover:bg-red-950/25' : 'bg-red-50/40 hover:bg-red-50/70')
    : isDelayed
      ? (dark ? 'bg-amber-950/15 hover:bg-amber-950/25' : 'bg-amber-50/30 hover:bg-amber-50/60')
      : (dark ? 'hover:bg-white/3' : 'hover:bg-slate-50/60')
  const rowBorder = dark ? 'border-white/5' : 'border-slate-100'
  const txtMain   = dark ? 'text-slate-100' : 'text-slate-800'
  const txtMuted  = dark ? 'text-slate-500' : 'text-slate-400'
  // La descripcion y el codigo del articulo iban en `txtMuted`, que sobre su
  // fondo da 2,6:1 en claro y 3,5:1 en oscuro — por debajo del minimo legible
  // (4,5:1) en los DOS temas, y ademas a 9-10 px. No se veian.
  //
  // No suben a negro a proposito: el nº de OP va en `txtMain` y en negrita, y es
  // el punto de entrada de la fila. Si la descripcion iguala ese peso, las dos
  // compiten y no se sabe por donde empezar a leer. Quedan tres escalones:
  // OP (800/100, bold) > descripcion (700/300) > codigo (500/400).
  const txtDesc   = dark ? 'text-slate-300' : 'text-slate-700'
  const txtCodigo = dark ? 'text-slate-400' : 'text-slate-500'
  const divBorder = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.10)'

  const highlightRing = highlighted
    ? (dark ? 'ring-2 ring-inset ring-sky-500/50' : 'ring-2 ring-inset ring-sky-400/50')
    : ''

  return (
    <div
      id={`gantt-row-${prod.produccion}`}
      // El aro sky lo pone la busqueda y el tinte fucsia la marca del usuario:
      // son dos cosas distintas y tienen que poder verse a la vez.
      className={`border-b last:border-0 ${rowBorder} ${rowBg} ${marcada ? (dark ? 'fila-marcada-dark' : 'fila-marcada') : ''} transition-colors ${highlightRing}`}
    >
      <div
        className="flex items-stretch cursor-pointer select-none"
        style={{ minHeight: 54 }}
        onClick={() => items.length && setManual(!open)}
      >
        {/* Info panel — ancho fijo */}
        <div
          className="shrink-0 flex items-center gap-2 px-3 py-2 border-r"
          style={{ width: ANCHO_INFO, borderColor: divBorder }}
        >
          <BotonMarca marcas={marcas} clave={claveOp} dark={dark} marcada={marcada} />
          <span className={`text-[9px] w-3 shrink-0 text-center ${txtMuted}`}>
            {items.length ? (open ? '▾' : '▸') : ''}
          </span>
          <span className={`w-2 h-2 rounded-full shrink-0 ${d.dot}`} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1 flex-wrap">
              <span className={`text-[11px] font-bold ${txtMain} truncate`}>{prod.produccion}</span>
              {/* Sólo con la fila CERRADA: abierta, el resaltado de la sublínea
                  ya dice por qué está aquí, y repetirlo en todas las filas
                  convertía la chapa en textura. */}
              {coincidenciasComp > 0 && !open && (
                <span
                  title={t('planning.gantt.componentMatchTip', { count: coincidenciasComp })}
                  className={`text-[9px] font-bold px-1 py-px rounded shrink-0 ${
                    dark ? 'bg-sky-900/60 text-sky-300 border border-sky-700/40'
                         : 'bg-sky-100 text-sky-700 border border-sky-200'
                  }`}
                >
                  ⌕{coincidenciasComp}
                </span>
              )}
              {/* El semáforo de material va ANTES que el contador de roturas:
                  dice de un vistazo si la orden se puede producir, y el ⚠N sólo
                  matiza cuántos componentes están en el peor caso. */}
              {estadoMaterial && (
                <span
                  className={`text-[9px] font-bold px-1 py-px rounded shrink-0 ${COMP_BADGE[estadoMaterial]}`}
                  title={t(`sequence.componentStatus.${estadoMaterial}`)}
                >
                  {t(`planning.consumo.estadoCorto.${estadoMaterial}`)}
                </span>
              )}
              {roturaCount > 0 && (
                <span className="text-[9px] font-bold text-red-500 bg-red-100 border border-red-200 px-1 py-px rounded shrink-0">
                  ⚠{roturaCount}
                </span>
              )}
              {isDelayed && (
                <span className={`text-[9px] font-bold px-1 py-px rounded shrink-0 ${
                  dark ? 'bg-amber-900/60 text-amber-400 border border-amber-700/40'
                       : 'bg-amber-100 text-amber-700 border border-amber-200'
                }`}>
                  {t('planning.gantt.delayed')}
                </span>
              )}
            </div>
            <div className={`text-[10px] ${txtDesc} line-clamp-2 leading-tight`} title={prod.nombreArticulo || fmtArt(prod.articulo)}>{prod.nombreArticulo || fmtArt(prod.articulo)}</div>
            {/* De 9 a 10 px: es un codigo que se teclea y se compara caracter a
                caracter, y el punto que separa articulo, color y estilo a 9 px
                casi no se distingue de una coma. */}
            {/* `font-semibold` y no `font-bold`: el nº de OP de arriba ya va en
                bold, y es el punto de entrada de la fila. A la referencia le hace
                falta cuerpo para leerse de un vistazo, no ganarle el sitio. */}
            <div className={`text-[10px] font-semibold ${txtCodigo} tabular-nums`}>
              {fmtArt(prod.articulo)}{prod.inventcolorid ? `·${prod.inventcolorid}` : ''}{prod.inventstyleid ? ` · ${prod.inventstyleid}` : ''}
            </div>
            {((isEmpresaAll && prod.inventlocationid) || prod.cantidadOperarios > 0) && (
              <div className="flex items-center gap-1.5 mt-0.5">
                {isEmpresaAll && prod.inventlocationid && (
                  <span className={`text-[9px] font-semibold px-1 py-px rounded ${
                    dark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600 border border-slate-200'
                  }`}>{prod.inventlocationid}</span>
                )}
                {prod.cantidadOperarios > 0 && (
                  <span className={`text-[9px] tabular-nums ${txtMuted}`}>{prod.cantidadOperarios} op.</span>
                )}
              </div>
            )}
          </div>
          <div className="shrink-0 text-right min-w-[52px]">
            <div className={`text-[11px] font-black tabular-nums ${txtMain}`}>{fmt(unidades)}</div>
            <div className={`text-[10px] ${txtMuted} tabular-nums`}>{fmt(pendientes)} p</div>
          </div>
        </div>

        {/* Carril de barras */}
        <CarrilTrack boundaries={track.boundaries} todayX={todayX} dark={dark}>
          {pos ? (
            <div
              className="absolute top-2 bottom-2 rounded overflow-hidden"
              style={{ left: `calc(${pos.left}% + 2px)`, width: `calc(${pos.width}% - 4px)`, minWidth: 6 }}
            >
              <div className={`absolute inset-0 ${isDelayed ? 'bg-amber-400' : d.bar} opacity-20`} />
              <div
                className={`absolute inset-y-0 left-0 ${isDelayed ? 'bg-amber-400' : d.bar} opacity-80`}
                style={{ width: `${avancePct}%` }}
              />
              {pos.width > track.minLabelPct && (
                <div className="absolute inset-0 flex items-center gap-1 px-1.5 overflow-hidden">
                  <span className="text-[9px] font-bold text-white/90 truncate">{avancePct}%</span>
                  {isDelayed && <span className="text-[9px] font-bold text-amber-200 shrink-0">↩</span>}
                  {hasRotura && <span className="text-[9px] font-bold text-red-200 shrink-0">⚠</span>}
                </div>
              )}
            </div>
          ) : (
            <div
              className="absolute inset-x-2 top-2 bottom-2 rounded border border-dashed flex items-center justify-center"
              style={{ borderColor: dark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)' }}
            >
              <span className={`text-[9px] ${txtMuted}`}>{t('planning.gantt.noDate')}</span>
            </div>
          )}
        </CarrilTrack>
      </div>

      {open && <ConsumoSubTable items={items} dark={dark} onSelect={onSelectComponent} resalta={resalta} clavePadre={claveOp} />}
    </div>
  )
}
